import React, { useMemo, useState } from "react";
import {
  Truck,
  Plus,
  Search,
  Pencil,
  Trash2,
  X,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import "../../styles/MovingTypes.css";

const initialMovingTypes = [
  {
    id: 1,
    name: "Residential Move",
    description: "Moving household items from one residence to another",
    status: "Active",
  },
  {
    id: 2,
    name: "Office Move",
    description: "Complete office and workplace relocation service",
    status: "Active",
  },
  {
    id: 3,
    name: "Commercial Move",
    description: "Commercial and business goods relocation",
    status: "Active",
  },
  {
    id: 4,
    name: "Vehicle Transportation",
    description: "Transportation of cars, bikes and other vehicles",
    status: "Active",
  },
  {
    id: 5,
    name: "Part Load",
    description: "Shared vehicle space for smaller moving requirements",
    status: "Active",
  },
  {
    id: 6,
    name: "Warehouse Move",
    description: "Moving goods between warehouses or storage facilities",
    status: "Active",
  },
  {
    id: 7,
    name: "Industrial Move",
    description: "Heavy equipment and industrial goods relocation",
    status: "Inactive",
  },
];

export default function MovingTypes() {
  const [movingTypes, setMovingTypes] = useState(
    initialMovingTypes
  );

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const rowsPerPage = 6;

  const filteredData = useMemo(() => {
    return movingTypes.filter((item) => {
      const searchValue = search.toLowerCase();

      const matchesSearch =
        item.name.toLowerCase().includes(searchValue) ||
        item.description.toLowerCase().includes(searchValue);

      const matchesStatus =
        !statusFilter || item.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [movingTypes, search, statusFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredData.length / rowsPerPage)
  );

  const currentPage = Math.min(page, totalPages);

  const currentRows = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const activeCount = movingTypes.filter(
    (item) => item.status === "Active"
  ).length;

  const inactiveCount = movingTypes.filter(
    (item) => item.status === "Inactive"
  ).length;

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  const toggleStatus = (id) => {
    setMovingTypes((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status:
                item.status === "Active"
                  ? "Inactive"
                  : "Active",
            }
          : item
      )
    );
  };

  const deleteMovingType = (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this moving type?"
    );

    if (!confirmed) return;

    setMovingTypes((prev) =>
      prev.filter((item) => item.id !== id)
    );
  };

  const handleAdd = () => {
    alert("Add Moving Type form will be connected here.");
  };

  const handleEdit = (item) => {
    alert(`Edit Moving Type: ${item.name}`);
  };

  return (
    <div className="moving-types-page">

      {/* Header */}
      <div className="moving-types-header">

        <div className="moving-types-title">

          <div className="moving-types-icon">
            <Truck size={22} />
          </div>

          <div>
            <h1>Moving Types</h1>
            <p>
              Manage moving categories used for quotations
            </p>
          </div>

        </div>

        <button
          className="moving-add-btn"
          onClick={handleAdd}
        >
          <Plus size={18} />
          Add Moving Type
        </button>

      </div>

      {/* Summary */}
      <div className="moving-summary-grid">

        <div className="moving-summary-card">

          <div className="moving-summary-icon total">
            <Truck size={20} />
          </div>

          <div>
            <span>Total Types</span>
            <strong>{movingTypes.length}</strong>
          </div>

        </div>

        <div className="moving-summary-card">

          <div className="moving-summary-icon active">
            <CheckCircle2 size={20} />
          </div>

          <div>
            <span>Active Types</span>
            <strong>{activeCount}</strong>
          </div>

        </div>

        <div className="moving-summary-card">

          <div className="moving-summary-icon inactive">
            <XCircle size={20} />
          </div>

          <div>
            <span>Inactive Types</span>
            <strong>{inactiveCount}</strong>
          </div>

        </div>

      </div>

      {/* Filters */}
      <div className="moving-filter-card">

        <div className="moving-search">

          <Search size={17} />

          <input
            type="text"
            placeholder="Search moving type..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />

        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>

        {(search || statusFilter) && (
          <button
            className="moving-clear-btn"
            onClick={clearFilters}
          >
            <X size={15} />
            Clear
          </button>
        )}

      </div>

      {/* Desktop Table */}
      <div className="moving-table-card">

        <div className="moving-table-wrapper">

          <table className="moving-table">

            <thead>
              <tr>
                <th>#</th>
                <th>Moving Type</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>

              {currentRows.length > 0 ? (
                currentRows.map((item, index) => (
                  <tr key={item.id}>

                    <td>
                      {(currentPage - 1) * rowsPerPage +
                        index +
                        1}
                    </td>

                    <td>
                      <div className="moving-name">

                        <div className="moving-row-icon">
                          <Truck size={16} />
                        </div>

                        <strong>{item.name}</strong>

                      </div>
                    </td>

                    <td>
                      <span className="moving-description">
                        {item.description}
                      </span>
                    </td>

                    <td>

                      <button
                        className={`moving-status ${
                          item.status === "Active"
                            ? "active"
                            : "inactive"
                        }`}
                        onClick={() =>
                          toggleStatus(item.id)
                        }
                      >
                        {item.status}
                      </button>

                    </td>

                    <td>

                      <div className="moving-actions">

                        <button
                          className="moving-action edit"
                          title="Edit"
                          onClick={() =>
                            handleEdit(item)
                          }
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          className="moving-action delete"
                          title="Delete"
                          onClick={() =>
                            deleteMovingType(item.id)
                          }
                        >
                          <Trash2 size={16} />
                        </button>

                      </div>

                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="moving-empty"
                  >
                    No moving types found
                  </td>
                </tr>
              )}

            </tbody>

          </table>

        </div>

        {/* Pagination */}
        <div className="moving-pagination">

          <span>
            Showing{" "}
            {filteredData.length === 0
              ? 0
              : (currentPage - 1) * rowsPerPage + 1}{" "}
            -{" "}
            {Math.min(
              currentPage * rowsPerPage,
              filteredData.length
            )}{" "}
            of {filteredData.length}
          </span>

          <div className="moving-pagination-buttons">

            <button
              disabled={currentPage === 1}
              onClick={() =>
                setPage((prev) =>
                  Math.max(1, prev - 1)
                )
              }
            >
              Previous
            </button>

            {Array.from(
              { length: totalPages },
              (_, index) => index + 1
            ).map((pageNumber) => (
              <button
                key={pageNumber}
                className={
                  currentPage === pageNumber
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setPage(pageNumber)
                }
              >
                {pageNumber}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() =>
                setPage((prev) =>
                  Math.min(
                    totalPages,
                    prev + 1
                  )
                )
              }
            >
              Next
            </button>

          </div>

        </div>

      </div>

      {/* Mobile */}
      <div className="moving-mobile-list">

        {currentRows.length > 0 ? (
          currentRows.map((item) => (
            <div
              className="moving-mobile-card"
              key={item.id}
            >

              <div className="moving-mobile-top">

                <div className="moving-name">

                  <div className="moving-row-icon">
                    <Truck size={16} />
                  </div>

                  <div>
                    <strong>{item.name}</strong>
                    <span>
                      {item.description}
                    </span>
                  </div>

                </div>

                <button
                  className={`moving-status ${
                    item.status === "Active"
                      ? "active"
                      : "inactive"
                  }`}
                  onClick={() =>
                    toggleStatus(item.id)
                  }
                >
                  {item.status}
                </button>

              </div>

              <div className="moving-mobile-actions">

                <button
                  className="mobile-moving-edit"
                  onClick={() =>
                    handleEdit(item)
                  }
                >
                  <Pencil size={15} />
                  Edit
                </button>

                <button
                  className="mobile-moving-delete"
                  onClick={() =>
                    deleteMovingType(item.id)
                  }
                >
                  <Trash2 size={15} />
                  Delete
                </button>

              </div>

            </div>
          ))
        ) : (
          <div className="moving-mobile-empty">
            No moving types found
          </div>
        )}

      </div>

    </div>
  );
}